// ————————————————————————————————————————————
// Diamond Viewer — Frame Engine Integration (Stage 2)
//
// This is the fullscreen overlay shell a shopper lands in after choosing a
// diamond. Stage 1 shipped the shell only (open/close, focus trap, static
// `image` stage, no Frame Engine wired in). This stage connects it to the
// real 360 experience:
//
//   Diamond Card -> Media Slot (diamond-card-media.js resolveDiamondMedia)
//                -> Frame Engine (diamond-frame-engine.js createFrameEngine)
//                -> this Viewer's stage <img>
//
// This file never computes a frame index, builds a frame URL, or loads a
// JPEG directly — every one of those stays the Frame Engine's job. It only
// ever: creates one engine instance per open() call, tells it to
// load()/play(), forwards raw pixel deltas from pointerdown/move/up to
// beginDrag()/updateDrag()/endDrag(), and hands whatever
// getDisplayFrameUrl() returns to the Frame Presenter (Buffered Frame
// Presenter, assets/diamond-frame-presenter.js — see that file's header)
// to actually reach the screen — this file decides *when* a frame is
// ready to paint, the presenter decides *how* it becomes visible (two
// layered <img> elements, prepared-then-revealed, instead of one <img>
// whose `.src` changes every frame).
//
// Autoplay control: autoplay starts on open. The first pointerdown
// pauses it immediately (beginDrag()). A drag release leaves the
// diamond on that frame and does NOT resume. A click/tap on the stage
// (movement at or below CLICK_DRAG_THRESHOLD_PX) toggles autoplay.
// A drag (movement above that threshold) is never treated as a click,
// so releasing a drag cannot immediately restart rotation. The stage
// Play/Pause control reads and writes this same shopper-owned intent —
// it is not a second autoplay state. Close / new diamond resets to
// the initial autoplay-on-open behavior.
//
// A diamond with no v360 media (or the rare case DiamondFrameEngine isn't
// loaded) falls back to the Stage 1 static `image` stage exactly as before
// — this file never assumes v360 data exists.
//
// Scope of this file:
//   1. Formatting          — plain data -> display strings, no DOM
//   2. Markup              — the shell's own HTML, built once per instance
//   3. <diamond-viewer>    — the custom element: open/close lifecycle,
//                            keyboard Escape, focus trap, body scroll lock,
//                            and the Frame Engine instance's full lifecycle
//                            (create on open, destroy on close/reopen)
//   4. Wiring              — listens for the existing `diamond:select`
//                            event (already dispatched by diamond-api.js's
//                            activateDiamondCard() on card activation)
//                            and a small window.DiamondViewer API for any
//                            future trigger to call directly
//
// Desktop vs. mobile layout, the backdrop, the two-pane grid, and the
  // stacked mobile layout are all CSS (assets/component-diamond-viewer.css) —
  // this file only ever toggles a single `is-open` class/`open` attribute and
  // never branches on viewport size itself. Mouse drag and mobile swipe share
  // one Pointer Events pipeline (pointerdown/move/up/cancel) rather than
  // separate mouse/touch listeners — both resolve to the same
  // engine.updateDrag(deltaPixels) call. Pointermove only stores the latest
  // pixel delta and schedules one requestAnimationFrame; the engine is
  // updated at most once per animation frame. Pointerup/cancel flush the
  // latest pending delta before endDrag() so the release frame is not lost.
// ————————————————————————————————————————————
(function (global, document) {
  "use strict";

  if (global.customElements && global.customElements.get("diamond-viewer")) return;

  const ORIGIN_LAB_GROWN = "lab-grown";

  // Same short codes Nivoda's certificate object uses (confirmed via the
  // filter wiring in diamond-api.js's *_LABEL_TO_API_VALUE maps) mapped
  // back to the shopper-facing label. Kept local/self-contained rather than
  // reading diamond-api.js's consts directly, so this file has no load-
  // order dependency on it beyond the `diamond:select` event contract.
  const CUT_LABELS = { ID: "Ideal", EX: "Excellent", VG: "Very Good", GD: "Good", FR: "Fair", F: "Fair" };
  const GRADE_LABELS = { EX: "Excellent", VG: "Very Good", GD: "Good", FR: "Fair", PR: "Poor" };
  const FLUORESCENCE_LABELS = {
    NON: "None",
    VSL: "Faint",
    SLT: "Faint",
    FNT: "Faint",
    MED: "Medium",
    STG: "Strong",
    STN: "Strong",
    VST: "Very Strong",
  };

  // Autoplay speed for the 360 stage: 12fps, tuned on top of the Buffered
  // Frame Presenter (assets/diamond-frame-presenter.js) rather than the
  // single-<img> src-swapping path this replaced.
  //
  // Visual A/B history, each round re-tested directly in the Viewer:
  //   - 5fps  (prior sustainable-ceiling setting): noticeably jerky.
  //   - 12fps (on single-<img> src swapping): smooth and calm, approved
  //     as that architecture's ceiling — manual drag still had headroom
  //     to improve, autoplay was good but not perfectly smooth.
  //   - Buffered Frame Presenter introduced (two layered <img>s,
  //     prepare-then-reveal instead of reassigning one visible <img>'s
  //     `.src`): manual drag became very smooth/realistic at the same
  //     12fps; autoplay improved but still hasn't fully closed the gap —
  //     some further presentation smoothness still looks possible.
  // Still a fixed, constant rate (no adaptive/per-tick logic).
  const AUTOPLAY_FPS = 15;

  // Click-vs-drag slop on the 360 stage. 8px is unchanged (~3 frames at
  // the engine's 2.5 px/frame accumulator), so a small twitch still counts
  // as a click / tap and ordinary micro-movement does not become a drag.
  // Movement above this Euclidean distance is a drag and must not toggle
  // autoplay.
  const CLICK_DRAG_THRESHOLD_PX = 8;

  // V360 Rolling Forward Buffer experiment (measured-root-cause audit):
  // how many frames immediately ahead of the start position must be
  // loaded before autoplay is allowed to begin (on open, and again
  // after an interaction-driven resume). Real-asset audit against 3
  // healthy Nivoda V360 assets (Round/Oval/Emerald) measured the prior
  // 2-frame reserve as only ~133ms of playback headroom at 15 FPS
  // against a real ~190-200ms per-frame network latency — the tiny
  // reserve was consumed almost immediately, then every asset reproduced
  // the same stall. Named START_THRESHOLD (was
  // INITIAL_BUFFER_FRAME_COUNT) to match the experiment doc; the engine
  // now also begins preparing forward frames the instant top_index loads
  // (see diamond-frame-engine.js's load()->maintainRollingBuffer() call),
  // independent of this gate, so waiting for 6 sequential frames here no
  // longer relies on the old radius-2 preloadAround() alone to ever fill
  // them. Still an experiment value, not a final product constant.
  const START_THRESHOLD = 6;

  // Safety cap on how long the buffer wait above may hold up autoplay.
  // A slow/degraded connection (or a short sequence that can't satisfy
  // the buffer at all) must never block playback indefinitely — past
  // this, autoplay starts on whatever is loaded so far, same as before
  // this change.
  const INITIAL_BUFFER_TIMEOUT_MS = 1200;

  // V360 Rolling Forward Buffer experiment: how many sequential frames
  // ahead of currentFrame, in the autoplay direction, the Frame Engine
  // continuously tries to keep LOADED/in-flight for as long as autoplay
  // is running (see diamond-frame-engine.js's maintainRollingBuffer()).
  // 15 frames ≈ 1.0s of forward reserve at the locked 15 FPS below —
  // deliberately larger than START_THRESHOLD's one-time startup gate,
  // since this is a continuously-replenished rolling target, not a
  // single up-front wait. Passed straight into createFrameEngine() as
  // config — the Frame Engine owns all queue/cache/network state for
  // this, per this experiment's architecture rule; the Viewer only ever
  // supplies the target number. Experiment value, not a final constant.
  const ROLLING_TARGET = 15;

  // Visual magnification of the displayed media only (Stage 5C11). Does
  // not change carat, diamond data, or the dialog size. 30–150% is a
  // visual-test range; ± buttons move in 10% steps, the slider is
  // continuous. Reset to 100% on every open()/_populate() so a newly
  // opened diamond always starts at default.
  const ZOOM_MIN = 30;
  const ZOOM_DEFAULT = 100;
  const ZOOM_MAX = 150;
  const ZOOM_STEP = 10;
  // Paint-only class. It does not close the Viewer. pagehide adds it when
  // the open Viewer is navigating to Diamond Details, so a restored Search
  // document does not paint the overlay. close() still performs cleanup.
  const RETURN_SUPPRESS_CLASS = "is-return-suppressed";

  // ————————————————————————————————————————————
  // 1) Formatting
  // ————————————————————————————————————————————
  function escapeHtml(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ));
  }

  function toTitleCase(value) {
    return String(value)
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function formatCarat(carat) {
    const n = Number(carat);
    return Number.isFinite(n) ? n.toFixed(2) : "";
  }

  // Mirrors diamond-api.js's formatDiamondPrice so card and viewer always
  // show the same backend retailPriceUsd. Format only — no pricing formula.
  function formatPrice(retailPriceUsd) {
    const rawPrice = Number(retailPriceUsd);
    if (!Number.isFinite(rawPrice)) return "";

    let displayAmount = rawPrice;
    if (global.Currency && typeof global.Currency.convert === "function") {
      try {
        const converted = Number(global.Currency.convert(rawPrice));
        if (Number.isFinite(converted)) displayAmount = converted;
      } catch (err) {
        // Ignore currency helper failures - fall back to rawPrice above.
      }
    }

    return `$${Math.round(displayAmount).toLocaleString("en-US")}`;
  }

  function formatGradeLabel(code, labelMap) {
    if (!code) return "";
    const key = String(code).trim().toUpperCase();
    return labelMap[key] || toTitleCase(code);
  }

  function formatOriginLabel(origin) {
    return origin === ORIGIN_LAB_GROWN ? "Lab Grown" : "Natural";
  }

  function buildTitle(d) {
    const caratLabel = formatCarat(d.carat);
    const shapeLabel = d.shape ? toTitleCase(d.shape) : "";
    const originLabel = `${formatOriginLabel(d.origin)} Diamond`;
    return [caratLabel && `${caratLabel} Carat`, shapeLabel, originLabel]
      .filter(Boolean)
      .join(" ");
  }

  // Same silhouette used by the result card's "no photo yet" state (see
  // assets/diamond-card-media.js PLACEHOLDER_HTML) so a diamond without an
  // image reads identically here as it does on the card it was opened
  // from, just at the viewer's larger stage size.
  const PLACEHOLDER_HTML = `<span class="diamond-viewer__placeholder" aria-hidden="true">
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 24 L32 8 L48 24 L32 58 Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
        <path d="M16 24 L48 24 M25 24 L32 58 M39 24 L32 58 M22 24 L32 32 L42 24" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>
      </svg>
    </span>`;

  const CLOSE_ICON_HTML = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6L6 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
      <path d="M6 6L18 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
    </svg>`;

  // Same Saved heart path as Search / Detail — outline by default,
  // filled via `.is-active` from DiamondSaved.applyHeartState.
  const SAVE_HEART_ICON_HTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"></path>
    </svg>`;

  const MISSING_FACT_VALUE = "\u2014";
  const MISSING_FACT_ACCESSIBLE = "Not provided";

  // Shape contract, not a payload-completeness check: Round always shows
  // Cut (with a dash when the grade is missing/unusable); every non-Round
  // shape shows Shape and never Cut, even if the provider sent FANCY or
  // another cut-like value. Polish / Symmetry / Fluorescence / Lab /
  // Origin stay on Diamond Detail, not in this compact Quick Viewer grid.
  function isRoundShape(shape) {
    return String(shape || "").trim().toUpperCase() === "ROUND";
  }

  function isUsableCutValue(code) {
    if (code == null) return false;
    const key = String(code).trim().toUpperCase();
    return Boolean(key) && key !== "FANCY";
  }

  function buildSpecRows(d) {
    const caratValue = formatCarat(d.carat);
    const caratDisplay = caratValue ? `${caratValue} ct` : "";
    const color = d.color || "";
    const clarity = d.clarity || "";

    if (isRoundShape(d.shape)) {
      const cutUsable = isUsableCutValue(d.cut);
      const cutLabel = cutUsable ? formatGradeLabel(d.cut, CUT_LABELS) : "";
      return [
        ["Carat", caratDisplay],
        cutLabel
          ? ["Cut", cutLabel]
          : ["Cut", MISSING_FACT_VALUE, MISSING_FACT_ACCESSIBLE],
        ["Color", color],
        ["Clarity", clarity],
      ];
    }

    return [
      ["Carat", caratDisplay],
      ["Shape", d.shape ? toTitleCase(d.shape) : ""],
      ["Color", color],
      ["Clarity", clarity],
    ];
  }

  // ————————————————————————————————————————————
  // 3) <diamond-viewer>
  // ————————————————————————————————————————————
  class DiamondViewer extends HTMLElement {
    constructor() {
      super();
      this._opener = null;
      this._built = false;
      this._onKeydown = this._onKeydown.bind(this);

      // Frame Engine wiring state (Stage 2). All null/cleared whenever no
      // v360 stage is active — see _buildFrameStage()/_teardownFrameEngine().
      this._frameEngine = null;
      this._frameStageEl = null;
      this._frameCanvasEl = null;
      this._frameCanvasAspect = 0;
      this._dragPointerId = null;
      this._dragStartX = 0;
      this._pointerStartY = 0;
      this._pointerIsDrag = false;
      // RAF-coalesced drag: pointermove stores the latest pixel delta and
      // schedules at most one animation frame. 0 means no RAF pending.
      this._pendingDragRaf = 0;
      this._pendingDragDelta = null;
      this._dragRafToken = 0;
      // Shopper-owned autoplay intent for this diamond session. True on
      // every new frame stage (open / reopen / new diamond) so initial
      // autoplay still starts; a drag forces it off, a click/tap toggles
      // it. Never restarted by a timer.
      this._autoplayEnabled = true;
      // Buffered Frame Presenter (assets/diamond-frame-presenter.js) —
      // owns the two layered <img> elements paint() hands resolved frame
      // srcs to. Null whenever no v360 stage is active, same lifecycle as
      // _frameEngine (created in _buildFrameStage(), released in
      // _teardownFrameEngine()).
      this._framePresenter = null;
      // Cleanup for a pending "wait for initial buffer, then play()" watch
      // — see _playWhenBuffered()/_cancelBufferWait(). Never more than one
      // outstanding at a time.
      this._bufferWaitCleanup = null;
      this._onFramePointerDown = this._onFramePointerDown.bind(this);
      this._onFramePointerMove = this._onFramePointerMove.bind(this);
      this._onFramePointerUp = this._onFramePointerUp.bind(this);
      this._onFramePointerCancel = this._onFramePointerCancel.bind(this);
      this._onAutoplayControlClick = this._onAutoplayControlClick.bind(this);
      this._onAutoplayControlPointerDown = this._onAutoplayControlPointerDown.bind(this);
      this._onSaveControlClick = this._onSaveControlClick.bind(this);
      this._onSaveControlPointerDown = this._onSaveControlPointerDown.bind(this);
      // Flat Search/Compare diamond currently open in this Viewer. Used
      // only for Saved heart id/snapshot — never a second Saved store.
      this._diamond = null;
      this._zoom = ZOOM_DEFAULT;
    }

    // Custom element constructors must not create/inspect children (spec-
    // enforced — Chrome throws "The result must not have children" if this
    // runs in the constructor), so the shell's DOM is built here instead,
    // the first time the element is actually inserted into the document.
    connectedCallback() {
      if (this._built) return;
      this._built = true;
      this._buildShell();
    }

    // Static shell markup only — the stage/panel are populated per-open in
    // _populate(). Built once so open()/close() never re-create the DOM.
    _buildShell() {
      this.innerHTML = `
        <div class="diamond-viewer__backdrop" data-diamond-viewer-backdrop></div>
        <div class="diamond-viewer__dialog" role="dialog" aria-modal="true" aria-label="Diamond details" tabindex="-1">
          <button type="button" class="diamond-viewer__close" data-diamond-viewer-close aria-label="Close">
            ${CLOSE_ICON_HTML}
          </button>
          <div class="diamond-viewer__content">
            <div class="diamond-viewer__media">
              <div class="diamond-viewer__toolbar" data-diamond-viewer-toolbar>
                <span class="diamond-viewer__spin" data-diamond-viewer-spin hidden aria-label="360-degree view">
                  <svg class="diamond-viewer__spin-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <path fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" d="M13.25 8A5.25 5.25 0 1 1 11.1 3.65"/>
                    <path fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" d="M11.3 2.1l.2 2.35 2.15-.85"/>
                  </svg>
                  <span class="diamond-viewer__spin-text" aria-hidden="true">360\u00b0</span>
                </span>
                <div class="diamond-viewer__toolbar-actions" data-diamond-viewer-toolbar-actions>
                  <button type="button" class="diamond-viewer__save" data-diamond-viewer-save data-save data-diamond-id="" aria-pressed="false" aria-label="Save diamond">
                    ${SAVE_HEART_ICON_HTML}
                  </button>
                  <button type="button" class="diamond-viewer__autoplay-btn" data-diamond-viewer-autoplay data-autoplay="off" hidden aria-label="Play 360 rotation">
                    <svg class="diamond-viewer__autoplay-icon diamond-viewer__autoplay-icon--pause" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                      <rect x="4.85" y="4.15" width="2.35" height="7.7" rx="0.4" fill="none" stroke="currentColor" stroke-width="1.15"/>
                      <rect x="8.8" y="4.15" width="2.35" height="7.7" rx="0.4" fill="none" stroke="currentColor" stroke-width="1.15"/>
                    </svg>
                    <svg class="diamond-viewer__autoplay-icon diamond-viewer__autoplay-icon--play" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                      <polygon points="5.45,3.95 5.45,12.05 13.25,8" fill="none" stroke="currentColor" stroke-width="1.15" stroke-linejoin="round" stroke-linecap="round"/>
                    </svg>
                  </button>
                  <span class="diamond-viewer__toolbar-divider" data-diamond-viewer-toolbar-divider hidden aria-hidden="true"></span>
                </div>
              </div>
              <div class="diamond-viewer__stage" data-diamond-viewer-stage></div>
              <div class="diamond-viewer__zoom" data-diamond-viewer-zoom dir="ltr">
                <button type="button" class="diamond-viewer__zoom-btn" data-diamond-viewer-zoom-out aria-label="Zoom out">
                  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <path d="M3 8h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
                <input type="range" class="diamond-viewer__zoom-slider" data-diamond-viewer-zoom-slider min="${ZOOM_MIN}" max="${ZOOM_MAX}" step="1" value="${ZOOM_DEFAULT}" aria-label="Diamond zoom">
                <button type="button" class="diamond-viewer__zoom-btn" data-diamond-viewer-zoom-in aria-label="Zoom in">
                  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <path d="M3 8h10M8 3v10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="diamond-viewer__panel" data-diamond-viewer-panel></div>
          </div>
        </div>
      `;

      this.dialogEl = this.querySelector(".diamond-viewer__dialog");
      this.backdropEl = this.querySelector("[data-diamond-viewer-backdrop]");
      this.closeBtn = this.querySelector("[data-diamond-viewer-close]");
      this.mediaEl = this.querySelector(".diamond-viewer__media");
      this.stageEl = this.querySelector("[data-diamond-viewer-stage]");
      this.panelEl = this.querySelector("[data-diamond-viewer-panel]");
      this.zoomEl = this.querySelector("[data-diamond-viewer-zoom]");
      this.zoomOutBtn = this.querySelector("[data-diamond-viewer-zoom-out]");
      this.zoomInBtn = this.querySelector("[data-diamond-viewer-zoom-in]");
      this.zoomSlider = this.querySelector("[data-diamond-viewer-zoom-slider]");
      this.autoplayBtn = this.querySelector("[data-diamond-viewer-autoplay]");
      this.saveBtn = this.querySelector("[data-diamond-viewer-save]");
      this.toolbarActionsEl = this.querySelector("[data-diamond-viewer-toolbar-actions]");
      this.spinEl = this.querySelector("[data-diamond-viewer-spin]");
      this.toolbarDividerEl = this.querySelector("[data-diamond-viewer-toolbar-divider]");

      this.closeBtn.addEventListener("click", () => this.close());
      if (this.autoplayBtn) {
        this.autoplayBtn.addEventListener("click", this._onAutoplayControlClick);
        this.autoplayBtn.addEventListener("pointerdown", this._onAutoplayControlPointerDown);
      }
      // Persistent shell heart — bind once. _populate() only updates id/state.
      if (this.saveBtn) {
        this.saveBtn.addEventListener("click", this._onSaveControlClick);
        this.saveBtn.addEventListener("pointerdown", this._onSaveControlPointerDown);
      }
      this.backdropEl.addEventListener("click", () => this.close());
      this.zoomOutBtn.addEventListener("click", () => this._setZoom(this._zoom - ZOOM_STEP));
      this.zoomInBtn.addEventListener("click", () => this._setZoom(this._zoom + ZOOM_STEP));
      this.zoomSlider.addEventListener("input", (event) => this._setZoom(event.target.value));
    }

    isOpen() {
      return this.classList.contains("is-open");
    }

    open(diamond, opener) {
      const d = diamond || {};
      this.classList.remove(RETURN_SUPPRESS_CLASS);
      this._opener = opener || document.activeElement;
      this._populate(d);

      this.classList.add("is-open");
      this.setAttribute("open", "");
      document.addEventListener("keydown", this._onKeydown);
      bodyScroll.lock(this.panelEl, {
        reserveScrollBarGap: true,
        // The lock preventDefaults every touchmove outside the facts
        // panel. That cancels iOS's native range gesture while leaving
        // minus/plus clicks intact. Allow the zoom strip only.
        allowTouchMove: (target) => {
          let el = target;
          if (el && el.nodeType === 3) el = el.parentElement;
          return !!(el && typeof el.closest === "function" && el.closest("[data-diamond-viewer-zoom]"));
        },
      });
      trapFocus(this, this.closeBtn);
    }

    close(options) {
      if (!this.isOpen()) return;
      // Cross-page history return must not focus the saved opener.
      // Focusing it can fight the browser's own scroll restoration.
      const historyReturn = !!(options && options.historyReturn === true);
      this.classList.remove("is-open");
      this.classList.remove(RETURN_SUPPRESS_CLASS);
      this.removeAttribute("open");
      document.removeEventListener("keydown", this._onKeydown);
      bodyScroll.unlock(this.panelEl);
      if (historyReturn) {
        removeTrapFocus();
      } else {
        removeTrapFocus(this._opener);
      }
      this._opener = null;
      // Item 11: close -> destroy() -> release listeners -> release timers
      // -> release engine. destroy() itself clears the autoplay interval
      // (via pause()) and its own emitter; this call also strips the
      // pointer listeners _buildFrameStage() attached and drops every
      // reference so nothing here can leak past this diamond's session.
      this._teardownFrameEngine();
    }

    // Custom elements are rarely removed from the document, but if this
    // one ever is, do not leave an autoplay timer or pointer listeners
    // running against a detached engine.
    disconnectedCallback() {
      this._teardownFrameEngine();
    }

    _onKeydown(event) {
      if (event.code && event.code.toUpperCase() === "ESCAPE") {
        event.preventDefault();
        this.close();
      }
    }

    // Visual magnification only — CSS --dv-zoom on the media column, read
    // by .diamond-viewer__image / .diamond-viewer__frame. Never writes
    // diamond data, never hits the API, never resizes the dialog.
    _setZoom(percent) {
      const next = Math.min(
        ZOOM_MAX,
        Math.max(ZOOM_MIN, Math.round(Number(percent)))
      );
      if (!Number.isFinite(next)) return;
      this._zoom = next;
      if (this.mediaEl) {
        this.mediaEl.style.setProperty("--dv-zoom", String(next / 100));
      }
      if (this.zoomSlider && this.zoomSlider.value !== String(next)) {
        this.zoomSlider.value = String(next);
      }
      if (this.zoomSlider) {
        this.zoomSlider.setAttribute("aria-valuetext", `${next} percent`);
      }
      const enabled = this.zoomEl && !this.zoomEl.hidden;
      if (this.zoomOutBtn) this.zoomOutBtn.disabled = !enabled || next <= ZOOM_MIN;
      if (this.zoomInBtn) this.zoomInBtn.disabled = !enabled || next >= ZOOM_MAX;
    }

    _setZoomEnabled(enabled) {
      if (this.zoomEl) this.zoomEl.hidden = !enabled;
      if (this.zoomSlider) this.zoomSlider.disabled = !enabled;
      if (this.zoomOutBtn) this.zoomOutBtn.disabled = !enabled || this._zoom <= ZOOM_MIN;
      if (this.zoomInBtn) this.zoomInBtn.disabled = !enabled || this._zoom >= ZOOM_MAX;
    }

    // The Play/Pause control is built once in the toolbar so its
    // listeners survive diamond switches. On the mobile card it stays
    // in that toolbar, clear of the diamond. On wider layouts it still
    // moves onto the frame canvas so the existing desktop icon tracks
    // the media rectangle. Hoist it back before any stageEl.innerHTML
    // replacement so the node is never destroyed.
    _prefersMobileToolbar() {
      try {
        return (
          typeof global.matchMedia === "function" &&
          global.matchMedia("(max-width: 989px)").matches
        );
      } catch (err) {
        return false;
      }
    }

    _autoplayHome() {
      return this.toolbarActionsEl || this.mediaEl;
    }

    _hoistAutoplayControl() {
      const btn = this.autoplayBtn;
      const home = this._autoplayHome();
      if (!btn || !home || typeof home.appendChild !== "function") return;
      if (btn.parentNode === home) return;
      const divider = this.toolbarDividerEl;
      if (
        divider &&
        divider.parentNode === home &&
        typeof home.insertBefore === "function"
      ) {
        home.insertBefore(btn, divider);
        return;
      }
      home.appendChild(btn);
    }

    _mountAutoplayControl(hostEl) {
      if (this._prefersMobileToolbar()) {
        this._hoistAutoplayControl();
        return;
      }
      if (!this.autoplayBtn || !hostEl || typeof hostEl.appendChild !== "function") {
        return;
      }
      hostEl.appendChild(this.autoplayBtn);
    }

    _applyFrameCanvasAspect(aspect) {
      if (!Number.isFinite(aspect) || aspect <= 0) return;
      const canvas = this._frameCanvasEl;
      if (!canvas) return;
      if (this._frameCanvasAspect === aspect) return;
      this._frameCanvasAspect = aspect;
      if (canvas.style && typeof canvas.style.setProperty === "function") {
        canvas.style.setProperty("--dv-media-aspect", String(aspect));
      }
      if (typeof canvas.setAttribute === "function") {
        canvas.setAttribute("data-aspect-ready", "");
      }
    }

    _syncFrameCanvasAspectFromEngine(engine) {
      if (!engine || typeof engine.getDisplayFrameUrl !== "function") return;
      const current =
        typeof engine.getCurrentFrame === "function" ? engine.getCurrentFrame() : 0;
      const result = engine.getDisplayFrameUrl(current);
      const img = result && result.image;
      if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
        this._applyFrameCanvasAspect(img.naturalWidth / img.naturalHeight);
      }
    }

    // Play/Pause is the same shopper-owned `_autoplayEnabled` flag the
    // stage click/drag path already uses. Hidden until a 360 engine is
    // actually interaction-ready so a loading prepared set cannot show
    // a usable control. No-op when the shell button is absent (tests
    // that mount `_buildFrameStage` directly without `_buildShell`).
    _syncAutoplayControl() {
      const btn = this.autoplayBtn;
      if (!btn) return;
      const usable = Boolean(this._frameEngine) && this._pocPreparedReady !== false;
      btn.hidden = !usable;
      if (this.spinEl) this.spinEl.hidden = !usable;
      if (this.toolbarDividerEl) this.toolbarDividerEl.hidden = !usable;
      const playing = usable && this._autoplayEnabled === true;
      btn.setAttribute("data-autoplay", playing ? "on" : "off");
      btn.setAttribute(
        "aria-label",
        playing ? "Pause 360 rotation" : "Play 360 rotation"
      );
    }

    // One control click must produce exactly one autoplay transition.
    // stopPropagation keeps this out of the stage pointer pipeline so
    // it cannot begin a drag or double-toggle through the click/tap
    // handler. Keyboard Space/Enter still fire this via the <button>.
    _onAutoplayControlClick(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (!this._frameEngine || this._pocPreparedReady === false) return;
      this._autoplayEnabled = !this._autoplayEnabled;
      if (this._autoplayEnabled) {
        this._playWhenBuffered(this._frameEngine, this._frameEngine.getCurrentFrame());
      } else {
        this._cancelBufferWait();
        if (typeof this._frameEngine.pause === "function") {
          this._frameEngine.pause();
        }
      }
      this._syncAutoplayControl();
    }

    _onAutoplayControlPointerDown(event) {
      if (!event) return;
      event.stopPropagation();
    }

    // Saved heart lives in the persistent shell (outside the frame stage).
    // Paint id + outline/filled from the single DiamondSaved store only.
    _syncSaveControl() {
      const btn = this.saveBtn;
      if (!btn) return;
      const diamond = this._diamond || {};
      const id =
        diamond.id == null || diamond.id === "" ? "" : String(diamond.id);
      if (typeof btn.setAttribute === "function") {
        btn.setAttribute("data-diamond-id", id);
      }
      if (btn.dataset) btn.dataset.diamondId = id;
      const Saved = global.DiamondSaved;
      if (Saved && typeof Saved.applyHeartState === "function") {
        Saved.applyHeartState(btn, id ? Saved.has(id) === true : false);
      }
    }

    // Match Search: save/remove through DiamondSaved. stopPropagation keeps
    // this out of the stage pointer pipeline (no drag / no stage toggle).
    // Never open()/populate()/teardown — Viewer media stays as-is.
    _onSaveControlClick(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      const Saved = global.DiamondSaved;
      const diamond = this._diamond;
      if (!Saved || !diamond) return;
      const id =
        diamond.id == null || diamond.id === "" ? "" : String(diamond.id);
      if (!id) return;
      let nowSaved;
      if (Saved.has(id)) {
        Saved.remove(id);
        nowSaved = false;
      } else {
        const snapshot =
          typeof Saved.buildSnapshot === "function"
            ? Saved.buildSnapshot(diamond)
            : {};
        Saved.save({ id: id, snapshot: snapshot });
        nowSaved = true;
      }
      if (typeof Saved.applyHeartState === "function") {
        Saved.applyHeartState(this.saveBtn, nowSaved);
      }
    }

    _onSaveControlPointerDown(event) {
      if (!event) return;
      event.stopPropagation();
    }

    _populate(d) {
      // Always release the previous diamond's engine/listeners first —
      // covers both a normal close() and selecting a new diamond while
      // this viewer is already open (open() is safe to call again without
      // an intervening close()).
      this._teardownFrameEngine();
      this._setZoom(ZOOM_DEFAULT);

      this._diamond = d || {};
      this._syncSaveControl();

      const title = buildTitle(d);
      this.dialogEl.setAttribute("aria-label", title || "Diamond details");

      // Media Slot (diamond-card-media.js) is the single source of truth
      // for what media a diamond has — this viewer never reads d.v360
      // directly, mirroring how the card itself resolves media.
      const media =
        (global.DiamondCardMedia && global.DiamondCardMedia.resolveDiamondMedia(d)) ||
        { image: d.image || null, v360: null };
      // Stage 5C2c: enter the frame path when Nivoda v360 exists and/or
      // preparedTurntable metadata is available. Manifest fetch stays
      // lazy inside resolveViewerMediaForActivation (viewer-open only).
      const PreparedViewer = global.DiamondPreparedTurntableViewer;
      const canPlayV360 = Boolean(
        global.DiamondFrameEngine &&
          ((PreparedViewer &&
            typeof PreparedViewer.canAttemptFrameViewer === "function" &&
            PreparedViewer.canAttemptFrameViewer(d, media.v360)) ||
            (media.v360 && media.v360.url))
      );

      this._setZoomEnabled(Boolean(canPlayV360 || d.image));

      if (canPlayV360) {
        // Generation token: a newer open()/close path must ignore stale
        // prepared-manifest results from a prior activation.
        this._populateGeneration = (this._populateGeneration || 0) + 1;
        const generation = this._populateGeneration;
        const showStaticFallback = () => {
          if (this._populateGeneration !== generation) return;
          if (d.image) {
            this.stageEl.innerHTML = `<img class="diamond-viewer__image" src="${escapeHtml(d.image)}" alt="${escapeHtml(title)}">`;
            this._setZoomEnabled(true);
          } else {
            this.stageEl.innerHTML = PLACEHOLDER_HTML;
            this._setZoomEnabled(false);
          }
          this._syncAutoplayControl();
        };
        const startStage = (v360Media) => {
          if (this._populateGeneration !== generation) return;
          const playable =
            (PreparedViewer &&
              typeof PreparedViewer.isPlayableV360Media === "function" &&
              PreparedViewer.isPlayableV360Media(v360Media)) ||
            (v360Media && v360Media.url);
          if (playable) {
            this._buildFrameStage(v360Media, title, d.image);
          } else {
            showStaticFallback();
          }
        };
        // After production prepared resolve (or pass-through), the gated
        // PoC adapter may still swap a raw Nivoda v360 when enabled.
        const afterPrepared = (v360Media) => {
          if (this._populateGeneration !== generation) return;
          if (
            !(v360Media && v360Media.__preparedTurntable) &&
            global.DiamondV360PocAdapter &&
            typeof global.DiamondV360PocAdapter.maybePrepareV360 === "function"
          ) {
            global.DiamondV360PocAdapter.maybePrepareV360(d, v360Media).then(startStage);
          } else {
            startStage(v360Media);
          }
        };

        if (
          PreparedViewer &&
          typeof PreparedViewer.resolveViewerMediaForActivation === "function"
        ) {
          PreparedViewer.resolveViewerMediaForActivation(d, media.v360, {
            isStale: () => this._populateGeneration !== generation,
          }).then((result) => {
            if (!result || result.stale) return;
            afterPrepared(result.media);
          });
        } else if (
          global.DiamondV360PocAdapter &&
          typeof global.DiamondV360PocAdapter.maybePrepareV360 === "function"
        ) {
          global.DiamondV360PocAdapter.maybePrepareV360(d, media.v360).then(startStage);
        } else {
          startStage(media.v360);
        }
      } else if (d.image) {
        this.stageEl.innerHTML = `<img class="diamond-viewer__image" src="${escapeHtml(d.image)}" alt="${escapeHtml(title)}">`;
      } else {
        this.stageEl.innerHTML = PLACEHOLDER_HTML;
      }

      const priceLabel = formatPrice(d.retailPriceUsd);
      const availability =
        d.availability === "available" ? "available" : "unavailable";
      const availabilityLabel =
        availability === "available" ? "Available" : "Unavailable";
      const specsHtml = buildSpecRows(d)
        .map(([label, value, accessible]) => {
          const valueHtml = accessible
            ? `<span class="diamond-viewer__spec-empty" aria-label="${escapeHtml(accessible)}">${escapeHtml(value)}</span>`
            : escapeHtml(value);
          return `
            <div class="diamond-viewer__spec">
              <dt>${escapeHtml(label)}</dt>
              <dd>${valueHtml}</dd>
            </div>`;
        })
        .join("");
      let detailUrl = "";
      try {
        if (
          global.IvanovAPI &&
          typeof global.IvanovAPI.buildDiamondDetailUrl === "function"
        ) {
          detailUrl = global.IvanovAPI.buildDiamondDetailUrl(d.id);
        }
      } catch (err) {
        detailUrl = "";
      }
      const detailCtaHtml = detailUrl
        ? `<div class="diamond-viewer__cta">
          <a class="diamond-viewer__details-cta" data-diamond-viewer-details href="${escapeHtml(detailUrl)}">
            View Diamond Details
          </a>
        </div>`
        : "";

      this.panelEl.innerHTML = `
        <p class="diamond-viewer__eyebrow">${escapeHtml(formatOriginLabel(d.origin))} Diamond${d.lab ? ` \u2022 ${escapeHtml(String(d.lab).toUpperCase())}` : ""}</p>
        <h2 class="diamond-viewer__title">${escapeHtml(title)}</h2>
        ${priceLabel ? `<p class="diamond-viewer__price">${escapeHtml(priceLabel)}</p>` : ""}
        <p class="diamond-viewer__availability" data-availability="${availability}">${availabilityLabel}</p>
        <hr class="diamond-viewer__divider">
        <dl class="diamond-viewer__specs">${specsHtml}</dl>
        ${detailCtaHtml}
      `;
      this.panelEl.scrollTop = 0;
    }

    // ————————————————————————————————————————————
    // Frame Engine lifecycle (Stage 2)
    //
    // One engine instance per open() call — see file header for the
    // Card -> Media Slot -> Frame Engine -> stage <img> chain. Nothing
    // below ever reads a v360 URL/index directly: `v360` here is only
    // ever handed straight to createFrameEngine() as `media`.
    // ————————————————————————————————————————————

    _buildFrameStage(v360, title, fallbackImage) {
      this._hoistAutoplayControl();
      this.stageEl.innerHTML = `
        <div class="diamond-viewer__frame-stage" data-diamond-viewer-frame-stage>
          <div class="diamond-viewer__frame-canvas" data-diamond-viewer-frame-canvas></div>
        </div>
      `;

      const frameStageEl = this.stageEl.querySelector("[data-diamond-viewer-frame-stage]");
      this._frameStageEl = frameStageEl;
      const canvasEl =
        (frameStageEl &&
          typeof frameStageEl.querySelector === "function" &&
          frameStageEl.querySelector("[data-diamond-viewer-frame-canvas]")) ||
        frameStageEl;
      this._frameCanvasEl = canvasEl;
      this._frameCanvasAspect = 0;
      this._autoplayEnabled = true;
      this._pocPreparedReady = false;
      this._pointerIsDrag = false;
      this._dragPointerId = null;
      this._cancelPendingDragRaf();
      this._syncAutoplayControl();

      // Buffered Frame Presenter (see assets/diamond-frame-presenter.js's
      // header for the full rationale and approval history): two layered
      // <img> elements inside frameStageEl instead of one <img> whose
      // `.src` this file used to mutate directly. The presenter owns only
      // how a resolved src reaches the screen (prepare on the hidden
      // layer, reveal once it's actually ready) — this file still never
      // computes a frame index/URL itself.
      const presenter = global.DiamondFramePresenter.createFramePresenter(canvasEl, {
        alt: title,
      });
      this._framePresenter = presenter;
      this._mountAutoplayControl(canvasEl);

      // rollingBufferTarget wires the Rolling Forward Buffer experiment's
      // ROLLING_TARGET into the Frame Engine — all cache/queue/network
      // state for it lives there (see diamond-frame-engine.js), never
      // duplicated here.
      //
      // Ivanov prepared media (production `__preparedTurntable` and the
      // existing PoC `__pocPrepared` path) uses complete-set preload
      // instead: disable the rolling buffer and hold the entire
      // presentation set in the load queue (default maxQueueSize is 16
      // and would drop the rest of a 64-frame preload). Raw Nivoda
      // media — including 256-frame sequences — must stay on the
      // rolling-buffer path and must not enter this gate.
      const isCompletePreloadMedia = Boolean(
        v360 && (v360.__pocPrepared || v360.__preparedTurntable)
      );
      const engine = global.DiamondFrameEngine.createFrameEngine({
        media: v360,
        rollingBufferTarget: isCompletePreloadMedia ? 0 : ROLLING_TARGET,
        maxQueueSize: isCompletePreloadMedia
          ? Math.max(64, Number(v360.frame_count) || 64)
          : undefined,
      });
      this._frameEngine = engine;

      // The only place this file paints a pixel — always via
      // getDisplayFrameUrl(), never a URL/index this file computed itself.
      //
      // Performance Audit tuning (image reuse, no architectural change):
      // getDisplayFrameUrl() already hands back the exact decoded `Image`
      // the engine fetched (result.image) — prefer its own resolved
      // `.src` over independently reading result.url, so this element is
      // always assigned the identical resource string a browser has
      // already fully decoded. Chrome's shared image cache already
      // absorbed this without it (0 duplicate fetches measured), but it
      // is not guaranteed under memory pressure or on Safari/iOS, so this
      // costs nothing and only helps there. The presenter itself skips
      // the assignment entirely when nothing changed — paint() runs on
      // every framestatechange too, so without that guard the same frame
      // could be handed to the presenter repeatedly while nothing new has
      // loaded.
      const paint = (index) => {
        const result = engine.getDisplayFrameUrl(index);
        if (!result) return;
        const img = result.image;
        if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
          this._applyFrameCanvasAspect(img.naturalWidth / img.naturalHeight);
        }
        const nextSrc = (img && img.src) || result.url;
        presenter.show(nextSrc);
      };

      engine.on("framechange", (payload) => paint(payload.index));
      // Covers the case getDisplayFrameUrl() already documents: a frame
      // that was mid-load when framechange fired (so a fallback frame was
      // painted instead) finishing load afterward — without this, the
      // held/stale frame would keep showing until the *next* index change.
      engine.on("framestatechange", (payload) => {
        if (
          payload.state === global.DiamondFrameEngine.FRAME_STATES.LOADED &&
          payload.index === engine.getCurrentFrame()
        ) {
          paint(payload.index);
        }
      });
      // A real V360 sequence whose very first (top_index) frame fails to
      // load — the engine has already stopped itself (state -> ERROR).
      // Fall back to the same static image/placeholder a non-v360 diamond
      // shows, so the shopper is never left staring at an empty stage.
      engine.on("error", () => {
        this._teardownFrameEngine();
        this.stageEl.innerHTML = fallbackImage
          ? `<img class="diamond-viewer__image" src="${escapeHtml(fallbackImage)}" alt="${escapeHtml(title)}">`
          : PLACEHOLDER_HTML;
        this._setZoomEnabled(Boolean(fallbackImage));
      });

      frameStageEl.addEventListener("pointerdown", this._onFramePointerDown);
      frameStageEl.addEventListener("pointermove", this._onFramePointerMove);
      frameStageEl.addEventListener("pointerup", this._onFramePointerUp);
      frameStageEl.addEventListener("pointercancel", this._onFramePointerCancel);

      engine.load().then(() => {
        // The viewer may have been closed/reopened with a different
        // diamond before this promise settled — _teardownFrameEngine()
        // already nulled _frameEngine in that case, so bail rather than
        // starting autoplay on an engine nothing references anymore.
        if (this._frameEngine !== engine) return;
        if (engine.getState() !== global.DiamondFrameEngine.STATES.IDLE) return;

        // Prepared media (production or PoC): preload the COMPLETE
        // presentation set before autoplay/interaction is judged —
        // removes hold-last-frame stalls from cold/un-decoded frames.
        // The 20s safety timeout is unchanged so a missing frame cannot
        // block the Viewer permanently. Raw Nivoda never enters here.
        if (isCompletePreloadMedia) {
          const n = engine.getFrameCount();
          const indices = [];
          for (let i = 0; i < n; i += 1) indices.push(i);
          engine.preload(indices);
          const started = Date.now();
          const poll = () => {
            if (this._frameEngine !== engine) return;
            let ready = true;
            for (let i = 0; i < n; i += 1) {
              if (
                engine.getFrameState(i) !==
                global.DiamondFrameEngine.FRAME_STATES.LOADED
              ) {
                ready = false;
                break;
              }
            }
            if (ready || Date.now() - started > 20000) {
              this._pocPreparedReady = true;
              this._syncFrameCanvasAspectFromEngine(engine);
              this._syncAutoplayControl();
              this._playWhenBuffered(engine, engine.getCurrentFrame());
              return;
            }
            global.setTimeout(poll, 30);
          };
          poll();
          return;
        }

        this._pocPreparedReady = true;
        this._syncFrameCanvasAspectFromEngine(engine);
        this._syncAutoplayControl();
        // Performance Audit tuning (initial preload buffer): don't
        // start autoplay the instant the top frame alone is loaded —
        // wait for a couple of frames ahead of it too (item 6/7).
        this._playWhenBuffered(engine, engine.getCurrentFrame());
      });
    }

    // Mouse drag and mobile swipe are the same gesture through Pointer
    // Events — one pipeline, no separate touch listeners. `isPrimary`
    // rejects extra touch points; the button check rejects a mouse
    // right/middle-click drag.
    _onFramePointerDown(event) {
      if (!this._frameEngine || !event.isPrimary) return;
      // Prepared media (production or PoC): ignore drag until the
      // complete set is LOADED (or the existing safety timeout fires).
      // Raw Nivoda never sets this flag to false.
      if (this._pocPreparedReady === false) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.preventDefault();
      // Cancel a pending first-autoplay buffer wait so a click/drag that
      // lands before play() cannot start rotation after the shopper has
      // already taken control. There is no timed autoplay-resume.
      this._cancelBufferWait();
      this._cancelPendingDragRaf();
      this._dragPointerId = event.pointerId;
      this._dragStartX = event.clientX;
      this._pointerStartY = Number.isFinite(event.clientY) ? event.clientY : 0;
      this._pointerIsDrag = false;
      // beginDrag() pauses autoplay itself (pointerdown -> stop
      // immediately) — this file never calls pause() directly.
      this._frameEngine.beginDrag();
      if (this._frameStageEl.setPointerCapture) {
        try {
          this._frameStageEl.setPointerCapture(event.pointerId);
        } catch (err) {
          // Pointer capture is a nice-to-have (keeps the drag tracking
          // even if the pointer leaves the stage element); its absence
          // never breaks the drag itself, since move/up are also bound
          // for as long as the pointer stays within the element.
        }
      }
    }

    _onFramePointerMove(event) {
      if (!this._frameEngine || this._dragPointerId !== event.pointerId) return;
      const dy = (Number.isFinite(event.clientY) ? event.clientY : 0) - this._pointerStartY;
      const distance = Math.hypot(event.clientX - this._dragStartX, dy);
      if (distance > CLICK_DRAG_THRESHOLD_PX) this._pointerIsDrag = true;
      // Sub-threshold movement is a click/tap in progress — do not rotate.
      if (!this._pointerIsDrag) return;
      this._scheduleDragUpdate(event.clientX - this._dragStartX);
    }

    _onFramePointerUp(event) {
      if (!this._frameEngine || this._dragPointerId !== event.pointerId) return;
      const wasDrag = this._pointerIsDrag;
      if (wasDrag) {
        this._pendingDragDelta = event.clientX - this._dragStartX;
        this._flushPendingDrag();
      } else {
        this._cancelPendingDragRaf();
      }
      this._dragPointerId = null;
      this._pointerIsDrag = false;
      // endDrag() does not restart autoplay itself — the engine simply
      // remains on whatever frame the gesture ended on (no reset to
      // top_index, no snap). A drag leaves autoplay stopped; a click/tap
      // toggles the shopper-owned autoplay intent.
      this._frameEngine.endDrag();
      if (wasDrag) {
        this._autoplayEnabled = false;
        this._syncAutoplayControl();
        return;
      }
      this._autoplayEnabled = !this._autoplayEnabled;
      if (this._autoplayEnabled) {
        this._playWhenBuffered(this._frameEngine, this._frameEngine.getCurrentFrame());
      }
      this._syncAutoplayControl();
    }

    // Interrupted gestures are not deliberate taps — stay on the current
    // frame and do not toggle autoplay back on.
    _onFramePointerCancel(event) {
      if (!this._frameEngine || this._dragPointerId !== event.pointerId) return;
      if (this._pointerIsDrag) {
        this._pendingDragDelta = event.clientX - this._dragStartX;
        this._flushPendingDrag();
      } else {
        this._cancelPendingDragRaf();
      }
      this._dragPointerId = null;
      this._pointerIsDrag = false;
      this._autoplayEnabled = false;
      this._frameEngine.endDrag();
      this._syncAutoplayControl();
    }

    // Coalesce pointermove into at most one engine.updateDrag() per
    // animation frame. Latest stored delta always wins.
    _scheduleDragUpdate(deltaPixels) {
      this._pendingDragDelta = deltaPixels;
      if (this._pendingDragRaf) return;
      const token = this._dragRafToken;
      this._pendingDragRaf = global.requestAnimationFrame(() => {
        this._onPendingDragRaf(token);
      });
    }

    _onPendingDragRaf(token) {
      this._pendingDragRaf = 0;
      if (token !== this._dragRafToken) return;
      if (!this._frameEngine || this._dragPointerId == null) {
        this._pendingDragDelta = null;
        return;
      }
      const delta = this._pendingDragDelta;
      this._pendingDragDelta = null;
      if (delta == null) return;
      this._frameEngine.updateDrag(delta);
    }

    // Apply the latest pending delta immediately, then drop any scheduled
    // RAF so a late callback cannot run after drag-end / teardown.
    _flushPendingDrag() {
      if (this._pendingDragRaf) {
        if (typeof global.cancelAnimationFrame === "function") {
          global.cancelAnimationFrame(this._pendingDragRaf);
        }
        this._pendingDragRaf = 0;
      }
      this._dragRafToken += 1;
      if (!this._frameEngine || this._pendingDragDelta == null) {
        this._pendingDragDelta = null;
        return;
      }
      const delta = this._pendingDragDelta;
      this._pendingDragDelta = null;
      this._frameEngine.updateDrag(delta);
    }

    _cancelPendingDragRaf() {
      if (this._pendingDragRaf) {
        if (typeof global.cancelAnimationFrame === "function") {
          global.cancelAnimationFrame(this._pendingDragRaf);
        }
        this._pendingDragRaf = 0;
      }
      this._dragRafToken += 1;
      this._pendingDragDelta = null;
    }

    // V360 Rolling Forward Buffer experiment (startup behavior, was
    // "Performance Audit tuning (initial preload buffer)"): delays
    // engine.play() until START_THRESHOLD frames ahead of `fromIndex`
    // are confirmed LOADED, or until INITIAL_BUFFER_TIMEOUT_MS elapses,
    // whichever comes first. Used for the very first autoplay start and
    // for an explicit click/tap that turns autoplay back on — never for
    // a timed post-drag resume. Never more than one wait outstanding —
    // a new call (or _cancelBufferWait()) always tears down the previous
    // one.
    _playWhenBuffered(engine, fromIndex) {
      this._cancelBufferWait();

      const frameCount = engine.getFrameCount();
      const bufferSize = Math.min(START_THRESHOLD, Math.max(0, frameCount - 1));
      const FRAME_STATES = global.DiamondFrameEngine.FRAME_STATES;
      const targets = [];
      for (let step = 1; step <= bufferSize; step += 1) {
        targets.push(global.DiamondFrameEngine.normalizeFrameIndex(fromIndex + step, frameCount));
      }
      const isBuffered = () =>
        targets.every((index) => engine.getFrameState(index) === FRAME_STATES.LOADED);

      const start = () => {
        this._cancelBufferWait();
        // The engine may have been destroyed/replaced, or a new
        // interaction (pointerdown) may have started, while this was
        // pending — both already call _cancelBufferWait(), but this
        // engine-identity check is the same defensive pattern used
        // everywhere else in this file for a promise/timer settling late.
        if (this._frameEngine !== engine) return;
        if (!this._autoplayEnabled) return;
        if (engine.getState() === global.DiamondFrameEngine.STATES.IDLE) {
          engine.play({ fps: AUTOPLAY_FPS }); // slow, calm autoplay (item 6)
        }
      };

      if (targets.length === 0 || isBuffered()) {
        start();
        return;
      }

      const off = engine.on("framestatechange", () => {
        if (isBuffered()) start();
      });
      const timer = global.setTimeout(start, INITIAL_BUFFER_TIMEOUT_MS);
      this._bufferWaitCleanup = () => {
        off();
        global.clearTimeout(timer);
      };
    }

    _cancelBufferWait() {
      if (!this._bufferWaitCleanup) return;
      const cleanup = this._bufferWaitCleanup;
      this._bufferWaitCleanup = null;
      cleanup();
    }

    _teardownFrameEngine() {
      // Close, diamond switch, and disconnectedCallback must cancel a
      // pending initial-buffer wait so it cannot start autoplay on an
      // engine this method is about to null out. There is no timed
      // autoplay-resume to clear. A pending drag RAF must also be
      // cancelled here so it cannot updateDrag() a closed or replaced
      // Viewer.
      this._cancelPendingDragRaf();
      this._cancelBufferWait();
      this._hoistAutoplayControl();
      this._autoplayEnabled = true;
      this._pointerIsDrag = false;
      this._frameCanvasEl = null;
      this._frameCanvasAspect = 0;
      if (this._frameStageEl) {
        this._frameStageEl.removeEventListener("pointerdown", this._onFramePointerDown);
        this._frameStageEl.removeEventListener("pointermove", this._onFramePointerMove);
        this._frameStageEl.removeEventListener("pointerup", this._onFramePointerUp);
        this._frameStageEl.removeEventListener("pointercancel", this._onFramePointerCancel);
      }
      this._frameStageEl = null;
      this._dragPointerId = null;
      if (this._framePresenter) {
        this._framePresenter.destroy(); // removes both layer <img> elements from the DOM
        this._framePresenter = null;
      }
      if (this._frameEngine) {
        this._frameEngine.destroy(); // clears its own autoplay timer + emitter
        this._frameEngine = null;
      }
      this._pocPreparedReady = undefined;
      this._syncAutoplayControl();
    }
  }

  customElements.define("diamond-viewer", DiamondViewer);

  // One-shot marker armed by Diamond Detail when that page was entered
  // from canonical Diamond Search. Detail X writes the same marker before
  // history.back(). Same key and schema as assets/diamond-detail.js.
  // Path and query only.
  const SEARCH_PATH = "/pages/diamond-search";
  const SEARCH_RETURN_MARKER_KEY = "ivanov:diamond-search:close-viewer";

  function searchReturnStorage() {
    try {
      return global.sessionStorage || null;
    } catch (err) {
      return null;
    }
  }

  function clearSearchReturnMarker() {
    const storage = searchReturnStorage();
    if (!storage || typeof storage.removeItem !== "function") return;
    try {
      storage.removeItem(SEARCH_RETURN_MARKER_KEY);
    } catch (err) {
      // Ignore blocked storage. The Viewer close below is the only work.
    }
  }

  function readSearchReturnMarker() {
    const storage = searchReturnStorage();
    if (!storage || typeof storage.getItem !== "function") return null;
    let raw = null;
    try {
      raw = storage.getItem(SEARCH_RETURN_MARKER_KEY);
    } catch (err) {
      return null;
    }
    if (raw == null) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return { malformed: true };
      }
      if (parsed.pathname !== SEARCH_PATH) return { malformed: true };
      if (typeof parsed.search !== "string") return { malformed: true };
      if (parsed.search !== "" && parsed.search.charAt(0) !== "?") return { malformed: true };
      if (parsed.search.length > 4096 || /[\r\n\u0000#]/.test(parsed.search)) {
        return { malformed: true };
      }
      return { pathname: SEARCH_PATH, search: parsed.search };
    } catch (err) {
      return { malformed: true };
    }
  }

  function searchReturnMarkerMatchesLocation(marker) {
    const loc = global.location;
    if (!loc || !marker) return false;
    return loc.pathname === SEARCH_PATH && loc.pathname === marker.pathname && loc.search === marker.search;
  }

  function dismissRestoredViewer() {
    const el = document.querySelector("diamond-viewer");
    if (!el || typeof el.close !== "function") return;
    if (typeof el.isOpen === "function" && !el.isOpen()) {
      if (el.classList) el.classList.remove(RETURN_SUPPRESS_CLASS);
      return;
    }
    el.close({ historyReturn: true });
  }

  function clearReturnSuppression() {
    const el = document.querySelector("diamond-viewer");
    if (!el || !el.classList) return;
    el.classList.remove(RETURN_SUPPRESS_CLASS);
  }

  // pageshow covers both a full load and a BFCache restoration. The listener
  // only removes a matching one-shot marker and closes an already-open Viewer.
  // A non-matching restoration drops the paint gate so a normal Viewer stays
  // visible. It does not close that Viewer.
  function consumeSearchReturnMarker() {
    const marker = readSearchReturnMarker();
    if (!marker) {
      clearReturnSuppression();
      return;
    }
    if (marker.malformed) {
      clearSearchReturnMarker();
      clearReturnSuppression();
      return;
    }
    if (!searchReturnMarkerMatchesLocation(marker)) {
      clearReturnSuppression();
      return;
    }
    clearSearchReturnMarker();
    dismissRestoredViewer();
  }

  let detailDeparturePending = false;

  function eventNode(event) {
    let node = event && event.target;
    if (node && node.nodeType === 3) node = node.parentNode;
    return node || null;
  }

  function detailAnchorFromEvent(event) {
    let node = eventNode(event);
    while (node && node !== document) {
      const tag = node.tagName ? String(node.tagName).toLowerCase() : "";
      if (
        tag === "a" &&
        typeof node.getAttribute === "function" &&
        node.getAttribute("data-diamond-viewer-details") != null
      ) {
        return node;
      }
      node = node.parentNode;
    }
    return null;
  }

  function viewerHostFrom(node) {
    let current = node;
    while (current) {
      const tag = current.tagName ? String(current.tagName).toLowerCase() : "";
      if (tag === "diamond-viewer" || typeof current.isOpen === "function") return current;
      current = current.parentNode;
    }
    return null;
  }

  function rememberDetailDeparture(event) {
    if (!event || event.defaultPrevented) {
      detailDeparturePending = false;
      return;
    }
    if (event.type !== "click") return;
    if (typeof event.button === "number" && event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = detailAnchorFromEvent(event);
    if (!anchor) return;
    if (typeof anchor.hasAttribute === "function" && anchor.hasAttribute("download")) return;
    const target = anchor.getAttribute("target");
    if (target && target !== "_self") return;
    const host = viewerHostFrom(anchor);
    if (!host || typeof host.isOpen !== "function" || !host.isOpen()) return;
    detailDeparturePending = true;
  }

  // Runs before the Search document is frozen. The class is already on the
  // restored DOM, so the first paint does not show the open overlay.
  // pageshow still closes through close({ historyReturn: true }).
  function suppressViewerBeforeFreeze() {
    const pending = detailDeparturePending;
    detailDeparturePending = false;
    if (!pending) return;
    const el = document.querySelector("diamond-viewer");
    if (!el || !el.classList) return;
    if (typeof el.isOpen === "function" && !el.isOpen()) return;
    el.classList.add(RETURN_SUPPRESS_CLASS);
  }

  if (!global.__ivanovSearchReturnPageshow && typeof global.addEventListener === "function") {
    global.__ivanovSearchReturnPageshow = true;
    global.addEventListener("click", rememberDetailDeparture);
    global.addEventListener("pagehide", suppressViewerBeforeFreeze);
    global.addEventListener("pageshow", consumeSearchReturnMarker);
  }

  // ————————————————————————————————————————————
  // 4) Wiring
  //
  // One `<diamond-viewer>` per document, created lazily on first use and
  // reused after that (mirrors global.js's ModalDialog: single instance,
  // appended to <body> so its fixed-position overlay is never clipped by
  // an ancestor's overflow/transform).
  // ————————————————————————————————————————————
  function getViewer() {
    let el = document.querySelector("diamond-viewer");
    if (!el) {
      el = document.createElement("diamond-viewer");
      document.body.appendChild(el);
    }
    return el;
  }

  // Card activation dispatches `diamond:select`; the native View Diamond
  // card anchor and the Viewer's native Detail anchor navigate independently.
  document.addEventListener("diamond:select", (event) => {
    getViewer().open(event.detail, document.activeElement);
  });

  global.DiamondViewer = {
    open: (diamond, opener) => getViewer().open(diamond, opener),
    close: () => {
      const el = document.querySelector("diamond-viewer");
      if (el) el.close();
    },
    SEARCH_RETURN_MARKER_KEY: SEARCH_RETURN_MARKER_KEY,
  };
})(window, document);
